import api from "../api";
import type { ConnectionItem } from "./connections";
import {
  readinessTag,
  type IntegrationDetail,
  type IntegrationStepItem,
} from "./integrationsBeta";
import { fetchAllPages, unwrapListResponse } from "./paginatedList";

// Core Client row projected for pickers (name = company_name).
export interface ClientItem {
  id: number;
  name: string;
  company_code?: string;
  is_active?: boolean;
  // Integrations need a backend auth.User for S3 storage; undefined = the
  // backend does not expose the flag (do not warn/block on it then).
  has_backend_user?: boolean;
}

export interface GetIntegrationClientsParams {
  search?: string;
  page_size?: number;
}

export interface ProviderItem {
  id: number;
  name: string;
  slug: string;
}

export interface ConnectionConfigSchemaByType {
  provider: string;
  type: string;
  config_schema: Record<string, unknown> | null;
}

export const registrationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getIntegrationClients: builder.query<
      ClientItem[],
      GetIntegrationClientsParams | void
    >({
      query: (params) => ({
        url: "/integration-platform/clients/",
        method: "GET",
        params: params ?? undefined,
      }),
      // Intentionally single-page: a server-search autocomplete (callers pass
      // `search` + `page_size`); every-row pickers use fetchAllPages instead.
      transformResponse: unwrapListResponse<ClientItem>,
    }),

    getIntegrationProviders: builder.query<ProviderItem[], void>({
      queryFn: (_arg, _api, _extra, fetchWithBQ) =>
        fetchAllPages<ProviderItem>(
          fetchWithBQ,
          "/integration-platform/providers/",
        ),
      providesTags: [{ type: "IntegrationProvider" as const, id: "LIST" }],
    }),

    createProvider: builder.mutation<ProviderItem, { name: string }>({
      query: (body) => ({
        url: "/integration-platform/providers/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "IntegrationProvider" as const, id: "LIST" }],
    }),

    createConnection: builder.mutation<
      ConnectionItem,
      {
        client: number;
        provider: number;
        type: string;
        name?: string;
        config: Record<string, unknown>;
      }
    >({
      query: (body) => ({
        url: "/integration-platform/connections/",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "IntegrationConnection" as const, id: "LIST" },
        readinessTag("ALL"),
      ],
    }),

    createIntegration: builder.mutation<
      IntegrationDetail,
      {
        client: number;
        name: string;
        description?: string;
        execution_target: string;
        timezone: string;
      }
    >({
      query: (body) => ({
        url: "/integration-platform/integrations/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "IntegrationMonitor", id: "LIST" }],
    }),

    createStep: builder.mutation<
      IntegrationStepItem,
      {
        integrationId: number;
        stage: string;
        stage_order: number;
        step_key: string;
        name: string;
        is_enabled?: boolean;
        connection: number | null;
        config: Record<string, unknown>;
      }
    >({
      query: ({ integrationId, ...body }) => ({
        url: `/integration-platform/integrations/${integrationId}/steps/`,
        method: "POST",
        // The backend pins the parent integration from the URL; sending it in
        // the body too is harmless and keeps older backends happy.
        body: { integration: integrationId, ...body },
      }),
      invalidatesTags: (_result, _err, { integrationId }) => [
        { type: "IntegrationMonitor" as const, id: integrationId },
        { type: "IntegrationMonitor" as const, id: "LIST" },
        readinessTag(integrationId),
      ],
    }),

    getConnectionConfigSchemaByType: builder.query<
      ConnectionConfigSchemaByType,
      { provider: string; type: string }
    >({
      query: ({ provider, type }) => ({
        url: "/integration-platform/connections/config-schema/",
        method: "GET",
        params: { provider, type },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetIntegrationClientsQuery,
  useGetIntegrationProvidersQuery,
  useCreateProviderMutation,
  useCreateConnectionMutation,
  useCreateIntegrationMutation,
  useCreateStepMutation,
  useLazyGetConnectionConfigSchemaByTypeQuery,
} = registrationApi;
